export type RuntimeEnv = Record<string, string | undefined>;
const ENV_ALIAS_REMOVAL_DATE = 'April 30, 2026';
const warnedDeprecatedRuntimeAliases = new Set<string>();

// Prefer Vite's injected env at build/runtime, but gracefully fall back to
// process.env so we can read server-side secrets in non-Vite contexts (e.g.,
// Vercel functions).
export const runtimeEnv: RuntimeEnv =
  (typeof import.meta !== 'undefined' && import.meta.env) ||
  (typeof process !== 'undefined' && process.env) ||
  {};

export const getEnvValue = (key: string): string | undefined => {
  const raw = runtimeEnv[key];
  return typeof raw === 'string' ? raw : undefined;
};

const normalizeRuntimeEnvValue = (value: string | undefined) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const normalizedPlaceholder = trimmed.toLowerCase();
  // Some deployment dashboards/scripts use textual placeholders for unset values.
  if (
    normalizedPlaceholder === "unset" ||
    normalizedPlaceholder === "undefined" ||
    normalizedPlaceholder === "null"
  ) {
    return null;
  }

  return trimmed;
};

const warnDeprecatedRuntimeAlias = (canonicalKey: string, aliasKey: string) => {
  const warningKey = `${canonicalKey}:${aliasKey}`;
  if (warnedDeprecatedRuntimeAliases.has(warningKey)) {
    return;
  }

  warnedDeprecatedRuntimeAliases.add(warningKey);
  console.warn(
    `[env] "${aliasKey}" is deprecated and will be removed after ${ENV_ALIAS_REMOVAL_DATE}. Use "${canonicalKey}" instead.`,
  );
};

export const getEnvValueFromKeys = (keys: readonly string[]): string | undefined => {
  const [canonicalKey, ...aliasKeys] = keys;
  if (!canonicalKey) {
    return undefined;
  }

  const canonicalValue = normalizeRuntimeEnvValue(getEnvValue(canonicalKey));
  if (canonicalValue) {
    return canonicalValue;
  }

  for (const aliasKey of aliasKeys) {
    const aliasValue = normalizeRuntimeEnvValue(getEnvValue(aliasKey));
    if (aliasValue) {
      warnDeprecatedRuntimeAlias(canonicalKey, aliasKey);
      return aliasValue;
    }
  }

  return undefined;
};

export const getCanonicalEnvValue = (
  canonicalKey: string,
  deprecatedKeys: readonly string[] = [],
): string | undefined => getEnvValueFromKeys([canonicalKey, ...deprecatedKeys]);
